import React, { useRef, useEffect, useState, useCallback } from "react";

// ============================================================
// VECTOR DRIFT — a mobile-first Asteroids-style game
// Left half of screen: virtual joystick (point = rotate, push = thrust)
// Right half: hold to fire. Desktop: arrows/WASD + space.
// ============================================================

const TAU = Math.PI * 2;
const SHIP_SIZE = 14;
const BULLET_SPEED = 6.5;
const BULLET_LIFE = 55;
const FIRE_COOLDOWN = 9;
const THRUST = 0.085;
const FRICTION = 0.992;
const MAX_SPEED = 6;
const TURN_RATE = 0.11;
const ROCK_SPEEDS = { 3: 0.9, 2: 1.4, 1: 2.0 };
const ROCK_RADII = { 3: 42, 2: 24, 1: 12 };
const ROCK_SCORES = { 3: 20, 2: 50, 1: 100 };

const rand = (a, b) => a + Math.random() * (b - a);

function makeRockShape(radius) {
  const points = [];
  const n = Math.floor(rand(9, 13));
  for (let i = 0; i < n; i++) {
    const ang = (i / n) * TAU;
    const r = radius * rand(0.72, 1.15);
    points.push({ x: Math.cos(ang) * r, y: Math.sin(ang) * r });
  }
  return points;
}

function makeRock(x, y, size) {
  const speed = ROCK_SPEEDS[size] * rand(0.7, 1.3);
  const dir = rand(0, TAU);
  return {
    x, y, size,
    r: ROCK_RADII[size],
    vx: Math.cos(dir) * speed,
    vy: Math.sin(dir) * speed,
    rot: rand(0, TAU),
    spin: rand(-0.02, 0.02),
    shape: makeRockShape(ROCK_RADII[size]),
  };
}

export default function AsteroidsGame() {
  const canvasRef = useRef(null);
  const [phase, setPhase] = useState("title"); // title | playing | gameover
  const [score, setScore] = useState(0);
  const [best, setBest] = useState(0);
  const [lives, setLives] = useState(3);
  const [wave, setWave] = useState(1);

  const g = useRef(null); // mutable game state
  const input = useRef({
    turn: 0, thrust: false, fire: false,
    joy: null, // {ox, oy, x, y, id}
    fireTouchId: null,
    keys: {},
  });
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  const spawnWave = (state, waveNum, w, h) => {
    const count = 3 + waveNum;
    for (let i = 0; i < count; i++) {
      // spawn away from the ship
      let x, y;
      do {
        x = rand(0, w); y = rand(0, h);
      } while (Math.hypot(x - state.ship.x, y - state.ship.y) < 160);
      state.rocks.push(makeRock(x, y, 3));
    }
  };

  const resetGame = useCallback(() => {
    const cv = canvasRef.current;
    const w = cv.clientWidth, h = cv.clientHeight;
    const state = {
      ship: { x: w / 2, y: h / 2, vx: 0, vy: 0, angle: -Math.PI / 2, inv: 120, dead: 0 },
      bullets: [], rocks: [], particles: [],
      cooldown: 0, shake: 0, wave: 1, score: 0, lives: 3,
      waveDelay: 0,
    };
    spawnWave(state, 1, w, h);
    g.current = state;
    setScore(0); setLives(3); setWave(1);
  }, []);

  const startGame = useCallback(() => {
    resetGame();
    setPhase("playing");
  }, [resetGame]);

  // ---------------- main loop ----------------
  useEffect(() => {
    const cv = canvasRef.current;
    const ctx = cv.getContext("2d");
    let raf;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      cv.width = cv.clientWidth * dpr;
      cv.height = cv.clientHeight * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const wrap = (o, w, h, pad = 0) => {
      if (o.x < -pad) o.x += w + pad * 2;
      if (o.x > w + pad) o.x -= w + pad * 2;
      if (o.y < -pad) o.y += h + pad * 2;
      if (o.y > h + pad) o.y -= h + pad * 2;
    };

    const burst = (state, x, y, n, speed, color) => {
      for (let i = 0; i < n; i++) {
        const d = rand(0, TAU), s = rand(0.4, speed);
        state.particles.push({
          x, y, vx: Math.cos(d) * s, vy: Math.sin(d) * s,
          life: rand(20, 45), max: 45, color,
        });
      }
    };

    const splitRock = (state, rock, idx) => {
      state.rocks.splice(idx, 1);
      state.score += ROCK_SCORES[rock.size];
      burst(state, rock.x, rock.y, rock.size * 6, 2.6, "#9be8ff");
      state.shake = Math.min(state.shake + rock.size * 2, 10);
      if (rock.size > 1) {
        for (let k = 0; k < 2; k++) {
          const child = makeRock(rock.x, rock.y, rock.size - 1);
          state.rocks.push(child);
        }
      }
      setScore(state.score);
    };

    const killShip = (state) => {
      const s = state.ship;
      burst(state, s.x, s.y, 26, 3.4, "#ffb15e");
      state.shake = 14;
      state.lives -= 1;
      setLives(state.lives);
      if (state.lives <= 0) {
        setBest((b) => Math.max(b, state.score));
        setPhase("gameover");
      } else {
        s.dead = 90; // respawn timer
      }
    };

    const step = () => {
      const w = cv.clientWidth, h = cv.clientHeight;
      const state = g.current;
      const inp = input.current;

      // ---------- background ----------
      ctx.clearRect(0, 0, w, h);
      const grad = ctx.createRadialGradient(w / 2, h / 2, 0, w / 2, h / 2, Math.max(w, h) * 0.75);
      grad.addColorStop(0, "#0b1026");
      grad.addColorStop(1, "#04060f");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);

      // faint starfield (deterministic per frame via fixed seed pattern)
      ctx.fillStyle = "rgba(155, 200, 255, 0.35)";
      for (let i = 0; i < 40; i++) {
        const sx = ((i * 97) % 113) / 113 * w;
        const sy = ((i * 57) % 89) / 89 * h;
        ctx.fillRect(sx, sy, 1.5, 1.5);
      }

      if (state && phaseRef.current === "playing") {
        // ---------- input mapping ----------
        const ship = state.ship;
        const keys = inp.keys;
        let turn = 0, thrust = false;
        if (keys.ArrowLeft || keys.a) turn -= 1;
        if (keys.ArrowRight || keys.d) turn += 1;
        if (keys.ArrowUp || keys.w) thrust = true;
        const firing = inp.fire || keys[" "] || keys.Space;

        // joystick: rotate ship toward stick direction, thrust if pushed far
        if (inp.joy) {
          const dx = inp.joy.x - inp.joy.ox;
          const dy = inp.joy.y - inp.joy.oy;
          const mag = Math.hypot(dx, dy);
          if (mag > 12) {
            const target = Math.atan2(dy, dx);
            let diff = target - ship.angle;
            while (diff > Math.PI) diff -= TAU;
            while (diff < -Math.PI) diff += TAU;
            ship.angle += Math.max(-TURN_RATE * 1.6, Math.min(TURN_RATE * 1.6, diff));
            if (mag > 42) thrust = true;
          }
        }
        ship.angle += turn * TURN_RATE;

        // ---------- ship physics ----------
        if (ship.dead > 0) {
          ship.dead -= 1;
          if (ship.dead === 0) {
            ship.x = w / 2; ship.y = h / 2; ship.vx = 0; ship.vy = 0;
            ship.angle = -Math.PI / 2; ship.inv = 130;
          }
        } else {
          if (thrust) {
            ship.vx += Math.cos(ship.angle) * THRUST;
            ship.vy += Math.sin(ship.angle) * THRUST;
            const sp = Math.hypot(ship.vx, ship.vy);
            if (sp > MAX_SPEED) {
              ship.vx = (ship.vx / sp) * MAX_SPEED;
              ship.vy = (ship.vy / sp) * MAX_SPEED;
            }
            // engine trail
            if (Math.random() < 0.7) {
              state.particles.push({
                x: ship.x - Math.cos(ship.angle) * SHIP_SIZE,
                y: ship.y - Math.sin(ship.angle) * SHIP_SIZE,
                vx: -Math.cos(ship.angle) * 1.6 + rand(-0.4, 0.4),
                vy: -Math.sin(ship.angle) * 1.6 + rand(-0.4, 0.4),
                life: 16, max: 16, color: "#ffb15e",
              });
            }
          }
          ship.vx *= FRICTION; ship.vy *= FRICTION;
          ship.x += ship.vx; ship.y += ship.vy;
          wrap(ship, w, h, SHIP_SIZE);
          if (ship.inv > 0) ship.inv -= 1;

          // ---------- firing ----------
          if (state.cooldown > 0) state.cooldown -= 1;
          if (firing && state.cooldown === 0) {
            state.bullets.push({
              x: ship.x + Math.cos(ship.angle) * SHIP_SIZE,
              y: ship.y + Math.sin(ship.angle) * SHIP_SIZE,
              vx: Math.cos(ship.angle) * BULLET_SPEED + ship.vx * 0.5,
              vy: Math.sin(ship.angle) * BULLET_SPEED + ship.vy * 0.5,
              life: BULLET_LIFE,
            });
            state.cooldown = FIRE_COOLDOWN;
          }
        }

        // ---------- bullets ----------
        for (let i = state.bullets.length - 1; i >= 0; i--) {
          const b = state.bullets[i];
          b.x += b.vx; b.y += b.vy; b.life -= 1;
          wrap(b, w, h);
          if (b.life <= 0) state.bullets.splice(i, 1);
        }

        // ---------- rocks ----------
        for (const r of state.rocks) {
          r.x += r.vx; r.y += r.vy; r.rot += r.spin;
          wrap(r, w, h, r.r);
        }

        // ---------- collisions ----------
        outer:
        for (let i = state.rocks.length - 1; i >= 0; i--) {
          const r = state.rocks[i];
          for (let j = state.bullets.length - 1; j >= 0; j--) {
            const b = state.bullets[j];
            if (Math.hypot(b.x - r.x, b.y - r.y) < r.r * 0.9) {
              state.bullets.splice(j, 1);
              splitRock(state, r, i);
              continue outer;
            }
          }
          if (ship.dead === 0 && ship.inv === 0 &&
              Math.hypot(ship.x - r.x, ship.y - r.y) < r.r * 0.85 + SHIP_SIZE * 0.6) {
            killShip(state);
          }
        }

        // ---------- wave clear ----------
        if (state.rocks.length === 0) {
          if (state.waveDelay === 0) state.waveDelay = 90;
          state.waveDelay -= 1;
          if (state.waveDelay === 1) {
            state.wave += 1;
            setWave(state.wave);
            spawnWave(state, state.wave, w, h);
            state.waveDelay = 0;
          }
        }

        // ---------- particles ----------
        for (let i = state.particles.length - 1; i >= 0; i--) {
          const p = state.particles[i];
          p.x += p.vx; p.y += p.vy; p.life -= 1;
          if (p.life <= 0) state.particles.splice(i, 1);
        }
        if (state.shake > 0) state.shake *= 0.88;
      }

      // ---------- render world ----------
      const state2 = g.current;
      if (state2) {
        ctx.save();
        if (state2.shake > 0.4) {
          ctx.translate(rand(-state2.shake, state2.shake), rand(-state2.shake, state2.shake));
        }

        // particles
        for (const p of state2.particles) {
          ctx.globalAlpha = p.life / p.max;
          ctx.fillStyle = p.color;
          ctx.fillRect(p.x - 1.2, p.y - 1.2, 2.4, 2.4);
        }
        ctx.globalAlpha = 1;

        // rocks — glowing vector outlines
        ctx.lineWidth = 1.6;
        for (const r of state2.rocks) {
          ctx.save();
          ctx.translate(r.x, r.y);
          ctx.rotate(r.rot);
          ctx.strokeStyle = "#9be8ff";
          ctx.shadowColor = "#4dd8ff";
          ctx.shadowBlur = 10;
          ctx.beginPath();
          r.shape.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
          ctx.closePath();
          ctx.stroke();
          ctx.restore();
        }

        // bullets
        ctx.fillStyle = "#ffffff";
        ctx.shadowColor = "#ffffff";
        ctx.shadowBlur = 8;
        for (const b of state2.bullets) {
          ctx.beginPath();
          ctx.arc(b.x, b.y, 2.2, 0, TAU);
          ctx.fill();
        }
        ctx.shadowBlur = 0;

        // ship
        const s = state2.ship;
        if (s.dead === 0 && phaseRef.current === "playing") {
          const blink = s.inv > 0 && Math.floor(s.inv / 6) % 2 === 0;
          if (!blink) {
            ctx.save();
            ctx.translate(s.x, s.y);
            ctx.rotate(s.angle);
            ctx.strokeStyle = "#eafcff";
            ctx.shadowColor = "#9be8ff";
            ctx.shadowBlur = 12;
            ctx.lineWidth = 1.8;
            ctx.beginPath();
            ctx.moveTo(SHIP_SIZE, 0);
            ctx.lineTo(-SHIP_SIZE * 0.7, SHIP_SIZE * 0.65);
            ctx.lineTo(-SHIP_SIZE * 0.35, 0);
            ctx.lineTo(-SHIP_SIZE * 0.7, -SHIP_SIZE * 0.65);
            ctx.closePath();
            ctx.stroke();
            ctx.restore();
          }
        }
        ctx.restore();
      }

      // ---------- joystick overlay ----------
      const joy = input.current.joy;
      if (joy && phaseRef.current === "playing") {
        ctx.strokeStyle = "rgba(155, 232, 255, 0.35)";
        ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.arc(joy.ox, joy.oy, 46, 0, TAU); ctx.stroke();
        const dx = joy.x - joy.ox, dy = joy.y - joy.oy;
        const mag = Math.min(Math.hypot(dx, dy), 46);
        const a = Math.atan2(dy, dx);
        ctx.fillStyle = "rgba(155, 232, 255, 0.5)";
        ctx.beginPath();
        ctx.arc(joy.ox + Math.cos(a) * mag, joy.oy + Math.sin(a) * mag, 18, 0, TAU);
        ctx.fill();
      }

      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);

  // ---------------- input handlers ----------------
  useEffect(() => {
    const down = (e) => {
      input.current.keys[e.key] = true;
      if (e.key === " ") e.preventDefault();
      if (phaseRef.current !== "playing" && (e.key === " " || e.key === "Enter")) startGame();
    };
    const up = (e) => { input.current.keys[e.key] = false; };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => { window.removeEventListener("keydown", down); window.removeEventListener("keyup", up); };
  }, [startGame]);

  const onTouchStart = (e) => {
    if (phaseRef.current !== "playing") return;
    const rect = canvasRef.current.getBoundingClientRect();
    for (const t of e.changedTouches) {
      const x = t.clientX - rect.left, y = t.clientY - rect.top;
      if (x < rect.width / 2 && !input.current.joy) {
        input.current.joy = { ox: x, oy: y, x, y, id: t.identifier };
      } else if (input.current.fireTouchId === null) {
        input.current.fireTouchId = t.identifier;
        input.current.fire = true;
      }
    }
  };
  const onTouchMove = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    for (const t of e.changedTouches) {
      const joy = input.current.joy;
      if (joy && t.identifier === joy.id) {
        joy.x = t.clientX - rect.left;
        joy.y = t.clientY - rect.top;
      }
    }
  };
  const onTouchEnd = (e) => {
    for (const t of e.changedTouches) {
      if (input.current.joy && t.identifier === input.current.joy.id) input.current.joy = null;
      if (t.identifier === input.current.fireTouchId) {
        input.current.fireTouchId = null;
        input.current.fire = false;
      }
    }
  };

  const mono = "'Courier New', ui-monospace, SFMono-Regular, Menlo, monospace";

  return (
    <div
      style={{
        position: "fixed", inset: 0, background: "#04060f",
        overflow: "hidden", touchAction: "none", userSelect: "none",
        WebkitUserSelect: "none", fontFamily: mono,
      }}
    >
      <canvas
        ref={canvasRef}
        style={{ width: "100%", height: "100%", display: "block" }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onTouchCancel={onTouchEnd}
      />

      {/* HUD */}
      {phase === "playing" && (
        <div style={{
          position: "absolute", top: 0, left: 0, right: 0,
          display: "flex", justifyContent: "space-between",
          padding: "14px 18px", pointerEvents: "none",
          color: "#9be8ff", letterSpacing: "0.15em", fontSize: 14,
          textShadow: "0 0 8px rgba(77,216,255,0.6)",
        }}>
          <span>{String(score).padStart(6, "0")}</span>
          <span>WAVE {wave}</span>
          <span>{"▲".repeat(Math.max(lives, 0))}</span>
        </div>
      )}

      {/* control hints */}
      {phase === "playing" && (
        <div style={{
          position: "absolute", bottom: 12, left: 0, right: 0,
          display: "flex", justifyContent: "space-between",
          padding: "0 24px", pointerEvents: "none",
          color: "rgba(155,232,255,0.35)", fontSize: 11, letterSpacing: "0.1em",
        }}>
          <span>◐ STEER + THRUST</span>
          <span>FIRE ◑</span>
        </div>
      )}

      {/* title screen */}
      {phase === "title" && (
        <Overlay>
          <h1 style={titleStyle}>VECTOR DRIFT</h1>
          <p style={subStyle}>an asteroids run</p>
          <p style={hintStyle}>
            LEFT SIDE — drag to steer, push far to thrust<br />
            RIGHT SIDE — hold to fire<br />
            keyboard: ← ↑ → + space
          </p>
          <StartButton onPress={startGame}>LAUNCH</StartButton>
        </Overlay>
      )}

      {/* game over */}
      {phase === "gameover" && (
        <Overlay>
          <h1 style={{ ...titleStyle, color: "#ffb15e", textShadow: "0 0 20px rgba(255,177,94,0.5)" }}>
            SHIP LOST
          </h1>
          <p style={subStyle}>score {String(score).padStart(6, "0")}</p>
          <p style={{ ...subStyle, opacity: 0.6 }}>best {String(Math.max(best, score)).padStart(6, "0")}</p>
          <StartButton onPress={startGame}>FLY AGAIN</StartButton>
        </Overlay>
      )}
    </div>
  );
}

const titleStyle = {
  color: "#9be8ff", fontSize: "clamp(28px, 8vw, 52px)", margin: 0,
  letterSpacing: "0.25em", fontWeight: 700,
  textShadow: "0 0 20px rgba(77,216,255,0.6)",
};
const subStyle = {
  color: "#eafcff", letterSpacing: "0.2em", margin: "10px 0 0",
  fontSize: 14, opacity: 0.85,
};
const hintStyle = {
  color: "rgba(155,232,255,0.55)", fontSize: 12, lineHeight: 1.9,
  letterSpacing: "0.08em", margin: "26px 0 0", textAlign: "center",
};

function Overlay({ children }) {
  return (
    <div style={{
      position: "absolute", inset: 0, display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      background: "rgba(4,6,15,0.55)", backdropFilter: "blur(2px)",
      padding: 24, textAlign: "center",
    }}>
      {children}
    </div>
  );
}

function StartButton({ children, onPress }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onPress}
      onTouchEnd={(e) => { e.preventDefault(); onPress(); }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        marginTop: 34, padding: "14px 44px",
        background: hover ? "rgba(155,232,255,0.15)" : "transparent",
        border: "1.5px solid #9be8ff", color: "#9be8ff",
        fontFamily: "inherit", fontSize: 16, letterSpacing: "0.3em",
        cursor: "pointer", borderRadius: 2,
        textShadow: "0 0 10px rgba(77,216,255,0.6)",
        boxShadow: "0 0 18px rgba(77,216,255,0.25), inset 0 0 18px rgba(77,216,255,0.08)",
        transition: "background 0.15s",
      }}
    >
      {children}
    </button>
  );
}
